/**
 * Phase 25 — /partner: public partner intake (Stage 2).
 * No login. Submissions land in the admin review queue
 * (GET /api/admin/submissions). Matches the site's monochrome language.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const TYPES = [
    ["property", "Hotel / Property"],
    ["itinerary", "Itineraries / Tours"],
    ["package", "Holiday packages"],
    ["ads", "Advertising"],
    ["other", "Something else"],
];

export default function PartnerPage() {
    const [form, setForm] = useState({
        company: "", contact_name: "", email: "", website: "",
        partner_type: "property", message: "",
    });
    const [state, setState] = useState("idle"); // idle | sending | done | error
    const [errMsg, setErrMsg] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async () => {
        setState("sending");
        setErrMsg("");
        try {
            const res = await fetch(`${BACKEND}/api/partners/apply`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (res.ok) {
                setState("done");
                return;
            }
            const data = await res.json().catch(() => ({}));
            setErrMsg(
                typeof data?.detail === "string"
                    ? data.detail
                    : "Please check the fields and try again.",
            );
            setState("error");
        } catch {
            setErrMsg("Network error — please try again.");
            setState("error");
        }
    };

    const valid =
        form.company.trim().length >= 2 &&
        form.contact_name.trim().length >= 2 &&
        /.+@.+\..+/.test(form.email) &&
        form.message.trim().length >= 10;

    return (
        <div className="min-h-screen bg-white">
            <div className="mx-auto max-w-xl px-4 py-10">
                <Link to="/" className="text-[12px] font-medium text-[#737373] hover:text-[#0A0A0A]">
                    ← Piqpath
                </Link>
                <h1 className="mt-4 text-[24px] font-bold tracking-tight text-[#0A0A0A]">
                    Partner with Piqpath
                </h1>
                <p className="mt-2 text-[13px] leading-relaxed text-[#525252]">
                    Feature your property, itineraries, or holiday packages — or run a
                    sponsored placement — in front of travellers while they plan.
                    Tell us a little about you and we&apos;ll get back within a few days.
                </p>

                {state === "done" ? (
                    <div className="mt-8 rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] p-5 text-center"
                         data-testid="partner-success">
                        <div className="text-[15px] font-semibold text-[#166534]">
                            Application received ✓
                        </div>
                        <p className="mt-1 text-[12.5px] text-[#166534]">
                            We&apos;ll review it and reply to {form.email}.
                        </p>
                    </div>
                ) : (
                    <div className="mt-8 space-y-4" data-testid="partner-form">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <input value={form.company} onChange={set("company")}
                                   placeholder="Company / brand *"
                                   data-testid="partner-company"
                                   className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-[13px] outline-none transition focus:border-[#0A0A0A]" />
                            <input value={form.contact_name} onChange={set("contact_name")}
                                   placeholder="Your name *"
                                   data-testid="partner-name"
                                   className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-[13px] outline-none transition focus:border-[#0A0A0A]" />
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <input value={form.email} onChange={set("email")} type="email"
                                   placeholder="Email *"
                                   data-testid="partner-email"
                                   className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-[13px] outline-none transition focus:border-[#0A0A0A]" />
                            <input value={form.website} onChange={set("website")}
                                   placeholder="Website (optional)"
                                   data-testid="partner-website"
                                   className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-[13px] outline-none transition focus:border-[#0A0A0A]" />
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="partner-type-row">
                            {TYPES.map(([val, label]) => (
                                <button key={val} type="button"
                                        onClick={() => setForm((f) => ({ ...f, partner_type: val }))}
                                        data-testid={`partner-type-${val}`}
                                        className={`rounded-full border px-3 py-1.5 text-[11.5px] font-medium transition ${form.partner_type === val ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        <textarea value={form.message} onChange={set("message")}
                                  rows={5}
                                  placeholder="What would you like to feature? Locations, dates, links… *"
                                  data-testid="partner-message"
                                  className="w-full rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-[13px] outline-none transition focus:border-[#0A0A0A]" />
                        {state === "error" && (
                            <p className="text-[12px] text-[#B91C1C]" data-testid="partner-error">{errMsg}</p>
                        )}
                        <button type="button" onClick={submit}
                                disabled={!valid || state === "sending"}
                                data-testid="partner-submit"
                                className="w-full rounded-lg bg-[#0A0A0A] px-4 py-3 text-[13px] font-semibold text-white transition disabled:opacity-40">
                            {state === "sending" ? "Sending…" : "Send application"}
                        </button>
                        <p className="text-center text-[11px] text-[#A3A3A3]">
                            Sponsored placements are always labelled. We reply to every application.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
